import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { motion } from "framer-motion";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { methodologyService, SubMethodology, SubMethodologyContent, ContentItem, ContentEntry, SubMethodologyMultiContent } from "../../lib/firebase";
import { useSystemSettings } from "../../lib/SystemSettingsContext";
import { Alert, AlertTitle, AlertDescription } from "../../components/ui/alert";

interface SubMethodologyDetailParams {
  methodologyId: string;
  subMethodologyId: string;
}

type ViewMode = 'list' | 'condensed';

export const SubMethodologyDetailPage = (): JSX.Element => {
  const { methodologyId = "straight-up", subMethodologyId } = useParams<
    keyof SubMethodologyDetailParams
  >() as SubMethodologyDetailParams;
  const location = useLocation();
  const { settings } = useSystemSettings();
  
  // Check if this is a new session from the URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const isNewSession = searchParams.get('newSession') === 'true';
  
  // Add debug logging for the newSession parameter
  console.log(`SubMethodologyDetailPage - isNewSession: ${isNewSession}, search: ${location.search}`);
  
  // Add a ref to track if we've already reset progress for this session
  const progressResetRef = useRef(false);
  
  const [userName, setUserName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subMethodology, setSubMethodology] = useState<SubMethodology | null>(null);
  const [contentEntries, setContentEntries] = useState<ContentEntry[]>([]);
  const [completedEntries, setCompletedEntries] = useState<string[]>([]);
  const [progress, setProgress] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const navigate = useNavigate();
  const auth = getAuth();

  // Check if this is the Pathway methodology and if it's disabled
  useEffect(() => {
    if (methodologyId === "pathway" && settings.disablePathway) {
      setError("The Pathway methodology is currently disabled by the administrator.");
      navigate('/dashboard');
    }
  }, [methodologyId, settings.disablePathway, navigate]);

  // Helper function to determine if an array is ContentItem[] or string[]
  const isContentItemArray = (array: any[]): array is ContentItem[] => {
    return array.length > 0 && typeof array[0] === 'object' && 'text' in array[0];
  };

  // Helper function to get the content text regardless of format
  const getContentText = (item: string | ContentItem): string => {
    if (typeof item === 'string') return item;
    return item.text;
  };

  // Function to navigate with preserving newSession parameter
  const navigateWithSessionParam = (path: string) => {
    if (isNewSession) {
      navigate(`${path}?newSession=true`);
    } else {
      navigate(path);
    }
  };

  // Add a helper to determine if the current methodology is guided
  const isGuided = subMethodology?.type === 'guided';

  // Authentication effect
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        console.log(`User authenticated in SubMethodologyDetailPage: ${user.uid}`);
        const displayName = user.displayName || user.email?.split('@')[0] || 'User';
        setUserName(displayName);
        localStorage.setItem('rawpa_user_name', displayName);
        
        // Ensure user progress is properly synchronized
        try {
          await methodologyService.ensureUserProgressSync();
        } catch (error) {
          console.error("Error syncing user progress:", error);
        }
      } else {
        console.log("User not authenticated, redirecting to signin");
        navigate('/signin');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Reset progress in the methodology document for new sessions
  const resetProgressInDocument = useCallback(async (foundSubMethodology: SubMethodology) => {
    if (!isNewSession || progressResetRef.current) return;
    
    try {
      console.log("Resetting progress in methodology document for new session");
      
      // Reset completedItems in the methodology document
      await methodologyService.updateSubMethodologyCompletion(
        methodologyId,
        subMethodologyId!,
        [] // Empty array to clear all completed items
      );
      
      // Mark that we've reset progress for this session
      progressResetRef.current = true;
      
      console.log("Progress reset successful in methodology document");
    } catch (error) {
      console.error("Error resetting progress in methodology document:", error);
    }
  }, [isNewSession, methodologyId, subMethodologyId]);

  // Add an effect to handle new sessions
  useEffect(() => {
    // For new sessions, reset the progress in the document
    if (isNewSession && !progressResetRef.current && subMethodology) {
      resetProgressInDocument(subMethodology);
    }
  }, [isNewSession, subMethodology, resetProgressInDocument]);

  // Load methodology and find sub-methodology
  useEffect(() => {
    console.group('SubMethodologyDetailPage - Loading Data');
    console.log(`Loading methodology ${methodologyId} for sub-methodology ${subMethodologyId}`);
    setLoading(true);
    
    // Use the new method to get all entries
    const loadAllEntries = async () => {
      try {
        // Get the methodology for the sub-methodology info
        const methodology = await methodologyService.getMethodologyById(methodologyId);
        if (!methodology) {
          setError("Methodology not found");
          setLoading(false);
          return false;
        }
        
        const foundSubMethodology = methodology.subMethodologies.find(
          (sm) => sm.id === subMethodologyId
        );
        
        if (!foundSubMethodology) {
          setError("Sub-methodology not found");
          setLoading(false);
          return false;
        }
        
        setSubMethodology(foundSubMethodology);
        
        // Get all content entries
        const allEntries = await methodologyService.getAllSubMethodologyEntries(methodologyId, subMethodologyId);
        console.log("Loaded all entries:", allEntries.length);
        
        if (allEntries.length > 0) {
          setContentEntries(allEntries);
          
          // If this is a new session, reset progress
          if (isNewSession) {
            console.log("Starting new session - resetting progress");
            setCompletedEntries([]);
            setProgress(0);
            
            // Save initial state to track this new session
            const sessionData = {
              methodologyId,
              subMethodologyId,
              title: foundSubMethodology.title || 'Methodology Session',
              completedItems: [],
              updatedAt: new Date().toISOString()
            };
            
            // Save to user's session data
            await methodologyService.saveUserProgress(methodologyId, 'straight-up', sessionData);
          } else {
          // Get completed entries from the completedItems in the detailedContent
          const completedEntriesList = foundSubMethodology.detailedContent?.completedItems || [];
          setCompletedEntries(completedEntriesList);
          
          // Calculate progress based on entry IDs, not entry count
          // We're tracking completion of entries by their IDs
          const entryIds = allEntries.map(entry => entry.id);
          const completedCount = completedEntriesList.filter(id => entryIds.includes(id)).length;
          setProgress(entryIds.length > 0 ? (completedCount / entryIds.length) * 100 : 0);
          }
          
          setLoading(false);
          console.groupEnd();
          return true;
        }
        
        // If no entries found, create a default one from detailedContent
        if (foundSubMethodology.detailedContent) {
          const content = foundSubMethodology.detailedContent;
          const entries: ContentEntry[] = [{
            id: content.name ? content.name.replace(/\s+/g, '-').toLowerCase() : 'default-entry',
            name: content.name || foundSubMethodology.title || 'Default Entry',
            definition: content.definition || '',
            keyPoints: content.keyPoints || [],
            commonBugs: content.commonBugs || [],
            pentestingTips: content.pentestingTips || [],
            tools: content.tools || [],
            extra: content.extra || ''
          }];
          
          setContentEntries(entries);
          
          // If this is a new session, reset progress
          if (isNewSession) {
            console.log("Starting new session with default entry - resetting progress");
            setCompletedEntries([]);
            setProgress(0);
            
            // Save initial state to track this new session
            const sessionData = {
              methodologyId,
              subMethodologyId,
              title: foundSubMethodology.title || 'Methodology Session',
              completedItems: [],
              updatedAt: new Date().toISOString()
            };
            
            // Save to user's session data
            await methodologyService.saveUserProgress(methodologyId, 'straight-up', sessionData);
          } else {
          // Set completed items from detailedContent
          const completedItemsList = content.completedItems || [];
          setCompletedEntries(completedItemsList);
          
          // Calculate progress
          const entryIds = entries.map(entry => entry.id);
          const completedCount = completedItemsList.filter(id => entryIds.includes(id)).length;
          setProgress(entryIds.length > 0 ? (completedCount / entryIds.length) * 100 : 0);
          }
        } else {
          // No entries and no detailedContent
          setContentEntries([]);
        }
        
        setLoading(false);
        console.groupEnd();
        return true;
      } catch (error) {
        console.error("Error loading entries:", error);
        return false;
      }
    };
    
    // Try to load all entries and fall back to subscription if that fails
    loadAllEntries().then(success => {
      if (!success) {
        console.log("Direct entries fetch failed, using subscription fallback");
        // Subscribe to the methodology in Firestore
        const unsubscribe = methodologyService.subscribeToMethodology(methodologyId, (methodologyData) => {
          console.log("Received methodology update:", methodologyData);
          if (methodologyData) {
            // Find the sub-methodology in the methodology
            const foundSubMethodology = methodologyData.subMethodologies.find(
              (sm) => sm.id === subMethodologyId
            );

            if (foundSubMethodology) {
              console.log("Found sub-methodology:", foundSubMethodology);
              setSubMethodology(foundSubMethodology);
              
              // Check if we have multiContent with entries
              if (foundSubMethodology.multiContent && foundSubMethodology.multiContent.entries && 
                foundSubMethodology.multiContent.entries.length > 0) {
                console.log("Found multiple content entries in subscription:", foundSubMethodology.multiContent.entries.length);
                
                // Map these entries to our ContentEntry format
                const entries = foundSubMethodology.multiContent.entries.map(entry => ({
                  id: entry.id,
                  name: entry.name || '',
                  definition: entry.definition || '',
                  keyPoints: entry.keyPoints || [],
                  commonBugs: entry.commonBugs || [],
                  pentestingTips: entry.pentestingTips || [],
                  tools: entry.tools || [],
                  extra: entry.extra || ''
                }));
                
                setContentEntries(entries);
                
                // If this is a new session, reset progress
                if (isNewSession) {
                  console.log("Starting new session via subscription - resetting progress");
                  setCompletedEntries([]);
                  setProgress(0);
                  
                  // Save initial state to track this new session
                  const sessionData = {
                    methodologyId,
                    subMethodologyId,
                    title: foundSubMethodology.title || 'Methodology Session',
                    completedItems: [],
                    updatedAt: new Date().toISOString()
                  };
                  
                  // Save to user's session data
                  methodologyService.saveUserProgress(methodologyId, 'straight-up', sessionData);
                } else {
                // Get any completed entries from the detailedContent
                const completedEntriesList = foundSubMethodology.detailedContent?.completedItems || [];
                setCompletedEntries(completedEntriesList);
                
                // Calculate progress based on entry IDs, not entry count
                const entryIds = entries.map(entry => entry.id);
                const completedCount = completedEntriesList.filter(id => entryIds.includes(id)).length;
                setProgress(entryIds.length > 0 ? (completedCount / entryIds.length) * 100 : 0);
                }
                
                setLoading(false);
                return;
              }
              
              // Get the content - either from detailedContent
              let content = foundSubMethodology.detailedContent;
              console.log("Detailed content:", content);
              
              // Convert content to entries
              const entries: ContentEntry[] = [];
              
              if (content && content.name) {
                // Create an entry from the content
                entries.push({
                  id: content.name.replace(/\s+/g, '-').toLowerCase(),
                  name: content.name,
                  definition: content.definition || '',
                  keyPoints: content.keyPoints || [],
                  commonBugs: content.commonBugs || [],
                  pentestingTips: content.pentestingTips || [],
                  tools: content.tools || [],
                  extra: content.extra || ''
                });
              } else if (content) {
                // If we don't have a name, create a generic entry with content
                entries.push({
                  id: 'default-entry',
                  name: foundSubMethodology.title || 'Default Entry',
                  definition: content.definition || '',
                  keyPoints: content.keyPoints || [],
                  commonBugs: content.commonBugs || [],
                  pentestingTips: content.pentestingTips || [],
                  tools: content.tools || [],
                  extra: content.extra || ''
                });
              } else {
                // Create a completely empty default entry
                entries.push({
                  id: 'default-entry',
                  name: foundSubMethodology.title || 'Default Entry',
                  definition: '',
                  keyPoints: [],
                  commonBugs: [],
                  pentestingTips: [],
                  tools: [],
                  extra: ''
                });
              }
              
              setContentEntries(entries);
              
              // If this is a new session, reset progress
              if (isNewSession) {
                console.log("Starting new session with detailed content - resetting progress");
                setCompletedEntries([]);
                setProgress(0);
                
                // Save initial state to track this new session
                const sessionData = {
                  methodologyId,
                  subMethodologyId,
                  title: foundSubMethodology.title || 'Methodology Session',
                  completedItems: [],
                  updatedAt: new Date().toISOString()
                };
                
                // Save to user's session data
                methodologyService.saveUserProgress(methodologyId, 'straight-up', sessionData);
              } else {
                // Set completed items from content
              const completedItemsList = content?.completedItems || [];
              setCompletedEntries(completedItemsList);
              
                // Calculate progress
              const entryIds = entries.map(entry => entry.id);
              const completedCount = completedItemsList.filter(id => entryIds.includes(id)).length;
              setProgress(entryIds.length > 0 ? (completedCount / entryIds.length) * 100 : 0);
              }
              
              setLoading(false);
            } else {
              console.error(`Sub-methodology ${subMethodologyId} not found in methodology ${methodologyId}`);
              setError("Sub-methodology not found");
            }
          } else {
            console.error(`Failed to load methodology ${methodologyId}`);
            setError("Failed to load methodology data");
          }
          setLoading(false);
        });

        return () => {
          console.log(`Cleaning up methodology ${methodologyId} subscription`);
          if (typeof unsubscribe === 'function') {
            unsubscribe();
          }
          console.groupEnd();
        };
      }
    });
  }, [methodologyId, subMethodologyId, isNewSession]);

  // Toggle completion status of a content entry
  const toggleEntryCompletion = async (entryId: string) => {
    try {
      // Update local state first for immediate UI feedback
      let newCompletedEntries: string[];
      
      if (completedEntries.includes(entryId)) {
        // Remove from completed
        newCompletedEntries = completedEntries.filter(id => id !== entryId);
      } else {
        // Add to completed
        newCompletedEntries = [...completedEntries, entryId];
      }
    
      // Update local state
    setCompletedEntries(newCompletedEntries);
    
    // Calculate new progress
    const entryIds = contentEntries.map(entry => entry.id);
    const completedCount = newCompletedEntries.filter(id => entryIds.includes(id)).length;
    setProgress(entryIds.length > 0 ? (completedCount / entryIds.length) * 100 : 0);
    
      // Save to Firestore
      await methodologyService.updateSubMethodologyCompletion(
        methodologyId,
        subMethodologyId,
        newCompletedEntries
      );
      
      // Once the user interacts with the content, it's no longer a "new session"
      // Save for session continuation regardless of initial new session state
      await methodologyService.saveUserProgress(
        methodologyId,
        'straight-up',
        {
          methodologyId,
          subMethodologyId,
          title: subMethodology?.title || 'Methodology Session',
          completedItems: newCompletedEntries,
          updatedAt: new Date().toISOString()
        }
      );
      
      console.log(`Entry ${entryId} completion toggled successfully`);
    } catch (error) {
      console.error("Error toggling entry completion:", error);
      setError("Failed to update progress. Please try again.");
    }
  };

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === 'list' ? 'condensed' : 'list');
  };

  // Render a content entry in list view
  const renderContentEntryListView = (entry: ContentEntry) => {
    const isCompleted = completedEntries.includes(entry.id);

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex items-start gap-3 mb-4">
          <div 
            className={`w-6 h-6 flex-shrink-0 border rounded cursor-pointer flex items-center justify-center transition-colors mt-1 ${
              isCompleted 
                ? "bg-[#daed6e] border-[#daed6e]" 
                : "border-gray-300 bg-white"
            }`}
            onClick={() => toggleEntryCompletion(entry.id)}
          >
            {isCompleted && (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-black" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586l-2.293-2.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l7-7a1 1 0 000-1.414z" clipRule="evenodd" />
              </svg>
            )}
          </div>
          <h2 className="font-['Space_Grotesk'] font-bold text-xl text-black">
            {entry.name}
          </h2>
        </div>

        {/* Definition */}
        {entry.definition && (
          <div className="mb-4">
            <h3 className="font-['Space_Grotesk'] font-medium text-lg text-black mb-2">Definition</h3>
            <p className="text-black opacity-80">{entry.definition}</p>
          </div>
        )}

        {/* Key Points */}
        {entry.keyPoints && entry.keyPoints.length > 0 && (
          <div className="mb-4">
            <h3 className="font-['Space_Grotesk'] font-medium text-lg text-black mb-2">Key Points</h3>
            <ul className="list-disc pl-6 space-y-1">
              {Array.isArray(entry.keyPoints) && entry.keyPoints.map((point, idx) => (
                <li key={idx} className="text-black opacity-80">
                  {typeof point === 'string' ? point : point.text}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Common Bugs */}
        {entry.commonBugs && entry.commonBugs.length > 0 && (
          <div className="mb-4">
            <h3 className="font-['Space_Grotesk'] font-medium text-lg text-black mb-2">Common Bugs</h3>
            <ul className="list-disc pl-6 space-y-1">
              {Array.isArray(entry.commonBugs) && entry.commonBugs.map((bug, idx) => (
                <li key={idx} className="text-black opacity-80">
                  {typeof bug === 'string' ? bug : bug.text}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Pentesting Tips */}
        {entry.pentestingTips && entry.pentestingTips.length > 0 && (
          <div className="mb-4">
            <h3 className="font-['Space_Grotesk'] font-medium text-lg text-black mb-2">Pentesting Tips</h3>
            <ul className="list-disc pl-6 space-y-1">
              {Array.isArray(entry.pentestingTips) && entry.pentestingTips.map((tip, idx) => (
                <li key={idx} className="text-black opacity-80">
                  {typeof tip === 'string' ? tip : tip.text}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Tools */}
        {entry.tools && entry.tools.length > 0 && (
          <div className="mb-4">
            <h3 className="font-['Space_Grotesk'] font-medium text-lg text-black mb-2">Tools</h3>
            <ul className="list-disc pl-6 space-y-1">
              {Array.isArray(entry.tools) && entry.tools.map((tool, idx) => (
                <li key={idx} className="text-black opacity-80">
                  {typeof tool === 'string' ? tool : tool.text}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Extra */}
        {entry.extra && (
          <div>
            <h3 className="font-['Space_Grotesk'] font-medium text-lg text-black mb-2">Additional Notes</h3>
            <p className="text-black opacity-80">{entry.extra}</p>
          </div>
        )}
      </div>
    );
  };

  // Render a content entry in condensed view
  const renderContentEntryCondensedView = (entry: ContentEntry) => {
    const isCompleted = completedEntries.includes(entry.id);
    
    // Count items in each category
    const keysCount = Array.isArray(entry.keyPoints) ? entry.keyPoints.length : 0;
    const bugsCount = Array.isArray(entry.commonBugs) ? entry.commonBugs.length : 0;
    const tipsCount = Array.isArray(entry.pentestingTips) ? entry.pentestingTips.length : 0;
    const toolsCount = Array.isArray(entry.tools) ? entry.tools.length : 0;

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
        <div className="flex items-start gap-3">
          <div 
            className={`w-6 h-6 flex-shrink-0 border rounded cursor-pointer flex items-center justify-center transition-colors mt-1 ${
              isCompleted 
                ? "bg-[#daed6e] border-[#daed6e]" 
                : "border-gray-300 bg-white"
            }`}
            onClick={() => toggleEntryCompletion(entry.id)}
          >
            {isCompleted && (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-black" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586l-2.293-2.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l7-7a1 1 0 000-1.414z" clipRule="evenodd" />
              </svg>
            )}
          </div>
          <div className="flex-1">
            <h2 className="font-['Space_Grotesk'] font-bold text-lg text-black">
              {entry.name}
            </h2>
            <div className="mt-1 flex flex-wrap gap-2">
              {keysCount > 0 && (
                <span className="text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
                  {keysCount} Key Points
                </span>
              )}
              {bugsCount > 0 && (
                <span className="text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
                  {bugsCount} Common Bugs
                </span>
              )}
              {tipsCount > 0 && (
                <span className="text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
                  {tipsCount} Pentesting Tips
                </span>
              )}
              {toolsCount > 0 && (
                <span className="text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
                  {toolsCount} Tools
                </span>
              )}
            </div>
            {entry.definition && (
              <p className="text-black opacity-80 text-sm mt-2 line-clamp-2">
                {entry.definition}
              </p>
            )}
          </div>
          <button
            onClick={() => navigateWithSessionParam(isGuided ? `/guided-methodologies/${methodologyId}/${subMethodologyId}/${entry.id}` : `/methodology/${methodologyId}/${subMethodologyId}/${entry.id}`)}
            className="text-xs bg-[#daed6e] text-black font-medium rounded-md px-2 py-1 hover:bg-[#c5d85c] transition-colors"
          >
            View
          </button>
        </div>
      </div>
    );
  };

  // Create sections from the sub-methodology content
  const renderContentSections = () => {
    if (!subMethodology) {
      return (
        <div className="text-center py-8 text-gray-500">
          No detailed content available for this methodology.
          <br />
          Please contact an administrator to add content.
        </div>
      );
    }

    if (contentEntries.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          No content entries available for this methodology.
          <br />
          Please contact an administrator to add content.
        </div>
      );
    }
    
    return (
      <div className="space-y-4">
        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex justify-between mb-2">
            <span className="text-sm font-medium">Progress</span>
            <span className="text-sm font-medium">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div 
              className="bg-[#daed6e] h-2.5 rounded-full transition-all duration-300 ease-out" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Content entries in selected view mode */}
        <div className={viewMode === 'list' ? "" : "grid grid-cols-1 md:grid-cols-2 gap-4"}>
          {contentEntries.map((entry, index) => (
            <div key={entry.id}>
              {viewMode === 'list' 
                ? renderContentEntryListView(entry)
                : renderContentEntryCondensedView(entry)
              }
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/dashboard')}>
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
            <div 
              onClick={handleSignOut}
              className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
            >
              <img className="w-6 h-6" alt="User" src="/user-icon.svg" />
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px] pb-12">
        {error && (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
            <p>{error}</p>
          </div>
        )}
        
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          {loading ? (
            <div className="text-center py-8">
              <p>Loading content...</p>
            </div>
          ) : (
            <>
              <div className="mb-8 flex justify-between items-center">
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <button 
                      onClick={() => navigateWithSessionParam(isGuided ? `/guided-methodologies/${methodologyId}` : `/methodology/${methodologyId}`)}
                      className="text-black opacity-60 hover:opacity-100 transition-opacity"
                    >
                      ← Back to Methodology
                    </button>
                  </div>
                  <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
                    {subMethodology?.title || "Methodology Details"}
                  </h1>
                  <p className="font-['Space_Grotesk'] text-sm text-black opacity-80">
                    {subMethodology?.description || "Explore this methodology in detail"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-black opacity-70">
                    {viewMode === 'list' ? 'List View' : 'Condensed View'}
                  </span>
                  <button
                    onClick={toggleViewMode}
                    className="bg-gray-100 rounded-full w-12 h-6 flex items-center p-1 transition-colors relative"
                  >
                    <div
                      className={`w-4 h-4 rounded-full bg-[#daed6e] absolute transform transition-transform ${
                        viewMode === 'condensed' ? 'translate-x-6' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>
              
              {renderContentSections()}
            </>
          )}
        </div>
      </div>
    </div>
  );
}; 